/**
 * Per-employee colours for the schedule.
 *
 * Assigned from a fixed list in order and never cycled randomly, so the same
 * person keeps the same colour between renders and reloads until someone picks
 * a different one. The defaults are spaced around the wheel rather than being
 * shades of the brand orange, the whole point is telling two people apart at a
 * glance, and the tiles already sit on an orange background.
 */
export const DEFAULT_EMPLOYEE_COLORS = [
  '#ff6500', // brand orange
  '#2d9cdb', // blue
  '#27ae60', // green
  '#bb6bd9', // violet
  '#f2c94c', // amber
  '#eb5757', // red
  '#00b8a9', // teal
  '#f2994a' // sand
]

/** Alpha of the colour wash behind a shift tile. */
const TILE_TINT_ALPHA = 0.45

/**
 * Darkening laid over the tint. The colours above are picked for contrast
 * against each other, not against white text, the scrim is what keeps the
 * label readable whichever colour lands underneath it.
 */
const TILE_SCRIM = 'rgba(0, 0, 0, 0.45)'

export const colorForEmployee = (
  colorsByUserId: Record<string, string>,
  userId: string,
  fallbackIndex: number
): string =>
  colorsByUserId[userId] ??
  DEFAULT_EMPLOYEE_COLORS[fallbackIndex % DEFAULT_EMPLOYEE_COLORS.length]

/** #rrggbb -> rgba(r, g, b, alpha); passes through anything already functional. */
export const withAlpha = (color: string, alpha: number): string => {
  const match = /^#?([\da-f]{6})$/i.exec(color.trim())
  if (!match) return color

  const [r, g, b] = [0, 2, 4].map(offset =>
    parseInt(match[1].slice(offset, offset + 2), 16)
  )

  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

/** Background for a shift tile: the employee's colour, dimmed for white text. */
export const tileBackground = (color: string): string =>
  `linear-gradient(${TILE_SCRIM}, ${TILE_SCRIM}), ${withAlpha(
    color,
    TILE_TINT_ALPHA
  )}`
